import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Alert,
  Grid,
  Button,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Support as SupportIcon,
  Notifications as NotificationsIcon,
  Description as DescriptionIcon,
  Settings,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon,
  Web as WebIcon,
} from '@mui/icons-material';

// Imports dos componentes de configurações
import {
  ParametrosGeral,
  ParametrosFinanceiro,
  ParametrosChamados,
  ParametrosNotificacoes,
  ParametrosCentral,
  ParametrosAtendimento,
  ParametrosMarketing,
  ParametrosHomepage,
} from './configuracoes';

// Imports dos tipos
import type {
  ParametroFinanceiro,
  ParametroChamados,
  ParametroNotificacoes,
} from './configuracoes/types';

// Definição das abas
const abas = [
  {
    id: 'geral',
    label: 'Geral',
    icon: <SettingsIcon fontSize="small" />,
    color: '#1976d2'
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    icon: <MoneyIcon fontSize="small" />,
    color: '#7b1fa2'
  },
  {
    id: 'chamados',
    label: 'Chamados',
    icon: <SupportIcon fontSize="small" />,
    color: '#d32f2f'
  },
  {
    id: 'notificacoes',
    label: 'Notificações',
    icon: <NotificationsIcon fontSize="small" />,
    color: '#ff9800'
  },
  {
    id: 'atendimento',
    label: 'Atendimento',
    icon: <WhatsAppIcon fontSize="small" />,
    color: '#25d366'
  },
  {
    id: 'central',
    label: 'Central do Assinante',
    icon: <PeopleIcon fontSize="small" />,
    color: '#009688'
  },
  {
    id: 'homepage',
    label: 'Homepage / Website',
    icon: <WebIcon fontSize="small" />,
    color: '#e91e63'
  },
  {
    id: 'marketing',
    label: 'Marketing Multinível',
    icon: <SettingsIcon fontSize="small" />,
    color: '#3f51b5'
  },
];

const ParametrosPage: React.FC = () => {
  const [abaAtiva, setAbaAtiva] = useState<string>('geral');

  const [parametrosChamados, setParametrosChamados] = useState<ParametroChamados>({
    auto_atribuicao: true,
    notificacao_email: true,
    notificacao_sms: false,
    tempo_sla: 24,
    categorias_padrao: ['Técnico', 'Financeiro', 'Comercial'],
  });



  const [parametrosNotificacoes, setParametrosNotificacoes] = useState<ParametroNotificacoes>({


    // Notificações Push/Sistema
    notificacoes_push: true,
    push_novo_cliente: true,
    push_novo_contrato: true,
    push_pagamento_recebido: false,
    push_chamado_criado: true,
    push_meta_vendas: false,

    // Configurações Gerais
    horario_inicio_notificacoes: '08:00',
    horario_fim_notificacoes: '18:00',
    nao_enviar_notificacoes_finais_semana: false,
    nao_enviar_notificacoes_feriados: true,
    envio_mensagens: true,
    notificacoes_lembretes: true,
  });

  // Função removida - configurações são salvas automaticamente

  const renderConteudoAba = () => {
    switch (abaAtiva) {
      case 'geral':
        return <ParametrosGeral />;

      case 'homepage':
        return <ParametrosHomepage />;

      case 'financeiro':
        return <ParametrosFinanceiro />;

      case 'chamados':
        return (
          <ParametrosChamados
            parametros={parametrosChamados}
            setParametros={setParametrosChamados}
          />
        );

      case 'notificacoes':
        return (
          <ParametrosNotificacoes
            parametros={parametrosNotificacoes}
            setParametros={setParametrosNotificacoes}
          />
        );

      case 'central':
        return <ParametrosCentral />;

      case 'atendimento':
        return <ParametrosAtendimento />;

      case 'marketing':
        return <ParametrosMarketing />;

      default:
        return (
          <Alert severity="info">
            Selecione uma aba para visualizar as configurações.
          </Alert>
        );
    }
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Cabeçalho */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
            Parâmetros do Sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure as principais definições e comportamentos do sistema
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Abas Laterais */}
        <Grid item xs={12} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: 0.5 }}>
              <List dense disablePadding>
                {abas.map((aba) => (
                  <React.Fragment key={aba.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={abaAtiva === aba.id}
                        onClick={() => setAbaAtiva(aba.id)}
                        sx={{
                          py: 0.4,
                          px: 1,
                          borderRadius: 1.5,
                          mx: 0.5,
                          my: 0.2,
                          transition: 'all 0.15s ease-in-out',
                          '&.Mui-selected': {
                            backgroundColor: `${aba.color}15`,
                            fontWeight: 'bold',
                            borderLeft: `3px solid ${aba.color}`,
                            '&:hover': {
                              backgroundColor: `${aba.color}25`,
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28, color: aba.color }}>
                          {aba.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={aba.label}
                          primaryTypographyProps={{
                            fontSize: '0.8125rem',
                            fontWeight: abaAtiva === aba.id ? 600 : 400,
                            color: abaAtiva === aba.id ? aba.color : 'text.primary'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Conteúdo da Aba */}
        <Grid item xs={12} md={9}>
          <Card elevation={2}>
            <CardContent sx={{ p: 1.5 }}>
              {renderConteudoAba()}

              {/* Configurações são salvas automaticamente */}
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                  💡 As configurações são salvas automaticamente quando alteradas
                </Alert>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ParametrosPage; 
